<template>
  <a-form-model
    ref="formRef"
    class="create-product-form"
    :model="{ attributes: typeAttributes, free_form_attributes: freeFormRows }"
    :colon="false"
    :aria-busy="isLoadingDraft"
  >
    <div
      v-if="isLoadingDraft"
      class="product-draft-loader"
      role="status"
      aria-live="polite"
    >
      <a-spin size="large" />
    </div>

    <ProductMainFields
      v-if="!isLoadingDraft"
      ref="mainFieldsRef"
      :productDraft="productDraft"
      :initialProductName="props.initialProductName"
      :attributeCount="typeAttributes.length + freeFormRows.length"
      @change="$emit('change', $event)"
      @typeChange="setProductType"
    />

    <a-alert
      v-if="!isLoadingDraft && !selectedType"
      type="info"
      showIcon
      :message="$t('offers.creation.select_type_for_attributes')"
      style="margin-bottom: 20px"
    />

    <a-form-model-item
      prop="attributes"
      v-if="!isLoadingDraft && !isLoadingAttributes && !attributesLoadError && selectedType"
    >
      <a-divider
        orientation="left"
        dashed
      >
        <span style="font-weight: 400">
          {{ $t("offers.create_form_fields.attributes_by_type") }}:
        </span>
        <span class="product-attributes-type">
          {{ selectedTypeName }}
        </span>
      </a-divider>

      <div class="product-attributes product-attributes--type">
        <div
          v-for="attr in typeAttributes"
          :key="attr.code"
          class="product-attribute-row"
        >
          <AttributeSelectRow
            v-if="attr.is_defined_list"
            :attribute="attr"
            :showValidation="props.showValidation"
            @change="updateAttribute"
          />
          <AttributeInputRow
            v-else
            :attribute="attr"
            :showValidation="props.showValidation"
            @change="updateAttribute"
          />
        </div>
      </div>
    </a-form-model-item>

    <a-form-model-item
      v-if="!isLoadingDraft && !isLoadingAttributes && !attributesLoadError && selectedType"
      prop="free_form_attributes"
    >
      <a-divider
        orientation="left"
        dashed
      >
        <span style="font-weight: 400">
          {{ $t("offers.create_form_fields.free_form_attributes") }}:
        </span>
        <span class="product-attributes-counter">
          {{ freeFormRows.length }}
        </span>
        <span class="secondary-text">{{ ` / 20` }}</span>
      </a-divider>

      <div class="product-attributes">
        <FreeFormAttributeRow
          v-for="row in freeFormRows"
          :key="row.id"
          :attribute="row.attribute"
          :showValidation="showFreeFormValidation"
          :unavailableCodes="getUnavailableAttributeCodes(row.id)"
          :data-incomplete-free-form="!row.attribute.code || !hasAttributeValue(row.attribute)"
          @change="updateFreeFormAttribute(row.id, $event)"
          @remove="removeFreeFormAttribute(row.id)"
        />
      </div>

      <a-button
        v-if="freeFormRows.length < 20"
        type="primary"
        @click="addFreeFormAttribute"
      >
        {{ $t("common.add") }}
      </a-button>
    </a-form-model-item>
    <div
      v-if="isLoadingAttributes"
      class="product-draft-loader"
      role="status"
    >
      <a-spin />
    </div>
    <a-alert
      v-if="attributesLoadError"
      type="error"
      showIcon
      :message="$t('common.error')"
    >
      <template #description>
        <a-button @click="setProductType(selectedType)">
          {{ $t("offers.creation.retry") }}
        </a-button>
      </template>
    </a-alert>
  </a-form-model>
</template>

<script lang="ts" setup>
import { type FormModel, message } from "ant-design-vue"
import { computed, nextTick, onMounted, ref, shallowRef } from "vue"
import { useRouter } from "vue-router/composables"

import i18n from "@/i18n"
import { fetchAttributesByType, fetchProductDraft } from "@/modules/offers/api/productModeration"
import type {
  FreeFormAttributeRowState,
  ProductDraftFormValues,
  ProductMainFieldsHandle
} from "@/modules/offers/types/createOffer"
import { ProductModerationAttribute, ProductModerationType } from "@/modules/offers/types/offers"
import type { ProductDraftResponse } from "@/modules/offers/types/productModeration"

import { loadTypeAttributes } from "../../utils/loadTypeAttributes"
import { hasAttributeValue, hasIncompleteFreeFormAttributes } from "../../utils/productAttributes"
import AttributeInputRow from "../ProductAttributes/AttributeInputRow.vue"
import AttributeSelectRow from "../ProductAttributes/AttributeSelectRow.vue"
import FreeFormAttributeRow from "../ProductAttributes/FreeFormAttributeRow.vue"

import ProductMainFields from "./ProductMainFields.vue"

type Emits = {
  (event: "change", product: ProductDraftFormValues): void
  (event: "ready", product: ProductDraftFormValues): void
  (event: "loadError"): void
}

const props = withDefaults(
  defineProps<{ initialProductName?: string; showValidation?: boolean }>(),
  {
    initialProductName: ""
  }
)
const emit = defineEmits<Emits>()

const router = useRouter()

const formRef = ref<FormModel>()
const typeAttributes = ref<ProductModerationAttribute[]>([])
const freeFormRows = ref<FreeFormAttributeRowState[]>([])
const showFreeFormValidation = ref(false)

let nextFreeFormRowId = 0

const createFreeFormRow = (attribute: ProductModerationAttribute): FreeFormAttributeRowState => ({
  id: nextFreeFormRowId++,
  attribute
})
const getFreeFormAttributes = () => freeFormRows.value.map((row) => row.attribute)

const productDraft = ref<ProductDraftResponse>()

const loadProductDraft = async () => {
  try {
    const { data } = await fetchProductDraft({ oid: router.currentRoute.params.oid })
    productDraft.value = data
    selectedType.value = data.type || undefined
    await setAttributes(data.attributes)
    await nextTick()
    const values = getDraftValues()
    // Назва з пошуку ще не збережена: початковий стан беремо з відповіді API.
    emit("ready", {
      ...values,
      public_title_mlang: {
        uk: data.public_title_mlang?.uk || "",
        ru: data.public_title_mlang?.ru || ""
      }
    })
    emit("change", values)
  } catch {
    emit("loadError")
  }
}

const selectedType = ref<ProductModerationType>()

const selectedTypeName = computed<string>(() => selectedType.value?.full_name || "")

const isLoadingAttributes = ref(false)
const attributesLoadError = ref(false)

let typeLoadId = 0

const setProductType = async (type?: ProductModerationType) => {
  const loadId = ++typeLoadId
  attributesLoadError.value = false
  selectedType.value = type
  typeAttributes.value = []
  freeFormRows.value = []
  showFreeFormValidation.value = false
  emitAttributeChanges()

  if (!type) {
    isLoadingAttributes.value = false
    return
  }

  isLoadingAttributes.value = true
  try {
    const attributes = await loadTypeAttributes(type.code)

    if (loadId !== typeLoadId) return

    typeAttributes.value = attributes
    emitAttributeChanges()
  } catch {
    if (loadId === typeLoadId) attributesLoadError.value = true
  } finally {
    if (loadId === typeLoadId) isLoadingAttributes.value = false
  }
}

const isLoadingDraft = shallowRef<boolean>(true)

const isLoading = computed(
  () => isLoadingDraft.value || isLoadingAttributes.value || attributesLoadError.value
)

const setAttributes = async (serverAttributes: ProductDraftResponse["attributes"]) => {
  const attributes: ProductModerationAttribute[] = serverAttributes.map((attr) => ({
    ...attr,
    value_type: attr.value_type as ProductModerationAttribute["value_type"],
    value: [attr.value].flat().filter(Boolean)
  }))

  if (!selectedType.value) {
    freeFormRows.value = attributes.map(createFreeFormRow)
    typeAttributes.value = []
    isLoadingDraft.value = false
    return
  }

  const { data } = await fetchAttributesByType({
    code: selectedType.value.code
  })

  const typeAttributeCodes = new Set(data.results.map((item) => item.code))
  const attributesByCode = new Map(attributes.map((attr) => [attr.code, attr]))

  freeFormRows.value = attributes
    .filter((attr) => !typeAttributeCodes.has(attr.code))
    .map(createFreeFormRow)
  typeAttributes.value = data.results.map((item) => attributesByCode.get(item.code)).filter(Boolean)

  isLoadingDraft.value = false
}
const addFreeFormAttribute = () => {
  if (freeFormRows.value.length >= 20) return

  freeFormRows.value.push(createFreeFormRow({ value: [] }))
  emitAttributeChanges()
}
const getUnavailableAttributeCodes = (rowId: number) => [
  ...typeAttributes.value.map((attribute) => attribute.code),
  ...freeFormRows.value
    .filter((row) => row.id !== rowId)
    .map((row) => row.attribute.code)
    .filter(Boolean)
]
const updateFreeFormAttribute = (rowId: number, attribute: ProductModerationAttribute) => {
  if (attribute.code && getUnavailableAttributeCodes(rowId).includes(attribute.code)) {
    return message.error(`${i18n.t("offers.product_moderation_errors.attribute_already_added")}`)
  }

  const row = freeFormRows.value.find((item) => item.id === rowId)
  row.attribute = attribute
  emitAttributeChanges()
}
const removeFreeFormAttribute = (rowId: number) => {
  freeFormRows.value = freeFormRows.value.filter((row) => row.id !== rowId)
  emitAttributeChanges()
}
const updateAttribute = (attribute: ProductModerationAttribute) => {
  const index = typeAttributes.value.findIndex(({ code }) => code === attribute.code)
  typeAttributes.value.splice(index, 1, attribute)

  if (!isLoadingDraft.value) emitAttributeChanges()
}
const validateFreeFormAttributes = () => {
  showFreeFormValidation.value = true
  const isValid = !hasIncompleteFreeFormAttributes(getFreeFormAttributes())

  if (!isValid) {
    nextTick(() => {
      formRef.value.$el
        .querySelector('[data-incomplete-free-form="true"]')
        ?.scrollIntoView({ behavior: "smooth", block: "center" })
    })
  }

  return isValid
}
const emitAttributeChanges = () => {
  emit("change", {
    attributes: [...typeAttributes.value, ...getFreeFormAttributes()]
  })
}
const scrollToFirstError = async () => {
  await nextTick()
  formRef.value.$el
    .querySelector(".has-error")
    ?.scrollIntoView({ behavior: "smooth", block: "center" })
}

const mainFieldsRef = ref<ProductMainFieldsHandle>()

const getDraftValues = (): ProductDraftFormValues => ({
  ...mainFieldsRef.value?.getDraftValues(),
  attributes: [...typeAttributes.value, ...getFreeFormAttributes()]
})
const getFormValues = async () => {
  const payload = await mainFieldsRef.value?.getFormValues()

  if (!payload) return null

  return {
    ...payload,
    attributes: typeAttributes.value,
    free_form_attributes: getFreeFormAttributes()
  }
}
const resetForm = () => {
  productDraft.value = undefined
  mainFieldsRef.value?.resetForm()
  typeAttributes.value = []
  freeFormRows.value = []
}

onMounted(loadProductDraft)

defineExpose({
  isLoading,
  resetForm,
  getFormValues,
  getDraftValues,
  scrollToFirstError,
  validateFreeFormAttributes
})
</script>

<style lang="scss" scoped>
.product-draft-loader {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 240px;
}

.product-attributes--type {
  .product-attribute-row {
    background-color: #fff;
  }

  .product-attribute-row:nth-child(even) {
    background-color: $gray-color-2;
  }
}

.product-attributes-counter {
  color: rgba(0, 0, 0, 0.85);
}
.product-attributes-type {
  font-weight: 500;
}

.secondary-text {
  color: $font-color-secondary;
}
</style>

<style lang="scss">
.create-product-form {
  & .ant-select-tree-switcher-noop {
    width: 6px !important;
  }
  & .ant-select-tree-node-content-wrapper {
    padding: 0;
  }
}
</style>
